import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Users, CreditCard, TrendingUp, Shield, CheckCircle, XCircle, Trash2, Gift, Tag, Plus, Clock, Star, Flag, MessageSquare } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: "overview", label: "Overview", icon: TrendingUp },
  { id: "approvals", label: "Pending Approvals", icon: Clock },
  { id: "chefs", label: "Chefs", icon: Users },
  { id: "transactions", label: "Transactions", icon: CreditCard },
  { id: "promos", label: "Promo Codes", icon: Tag },
  { id: "reviews", label: "Reviews", icon: MessageSquare },
];

export default function AdminDashboard() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [chefs, setChefs] = useState([]);
  const [pending, setPending] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [promos, setPromos] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [reviewFilter, setReviewFilter] = useState("all"); // all | flagged
  const [loading, setLoading] = useState(true);
  const [actionMsg, setActionMsg] = useState("");
  const [newCode, setNewCode] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [creatingPromo, setCreatingPromo] = useState(false);

  useEffect(() => { loadData(); }, []);
  useEffect(() => { loadReviews(); }, [reviewFilter]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, chefsRes, txRes, promosRes, pendingRes] = await Promise.all([
        axios.get(`${API}/api/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/api/admin/chefs`, { withCredentials: true }),
        axios.get(`${API}/api/admin/transactions`, { withCredentials: true }),
        axios.get(`${API}/api/admin/promo-codes`, { withCredentials: true }),
        axios.get(`${API}/api/admin/chefs/pending`, { withCredentials: true }),
      ]);
      setStats(statsRes.data);
      setChefs(chefsRes.data.chefs || []);
      setTransactions(txRes.data.transactions || []);
      setPromos(promosRes.data.promo_codes || []);
      setPending(pendingRes.data.chefs || []);
      await loadReviews();
    } catch {
      setActionMsg("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const loadReviews = async () => {
    try {
      const params = reviewFilter === "flagged" ? { flagged: true } : {};
      const { data } = await axios.get(`${API}/api/admin/reviews`, { params, withCredentials: true });
      setReviews(data.reviews || []);
    } catch {
      // silent
    }
  };

  const suspendChef = async (id) => {
    await axios.put(`${API}/api/admin/chefs/${id}/suspend`, {}, { withCredentials: true });
    setActionMsg("Chef suspended");
    loadData();
  };

  const activateChef = async (id) => {
    await axios.put(`${API}/api/admin/chefs/${id}/activate`, {}, { withCredentials: true });
    setActionMsg("Chef activated");
    loadData();
  };

  const deleteChef = async (id) => {
    if (!window.confirm("Delete this chef permanently?")) return;
    await axios.delete(`${API}/api/admin/chefs/${id}`, { withCredentials: true });
    setActionMsg("Chef deleted");
    loadData();
  };

  const grantFree = async (id) => {
    await axios.post(`${API}/api/admin/chefs/${id}/grant-free-membership`, {}, { withCredentials: true });
    setActionMsg("Free membership granted");
    loadData();
  };

  const revokeFree = async (id) => {
    if (!window.confirm("Revoke this chef's free membership? Their listing will become inactive.")) return;
    await axios.post(`${API}/api/admin/chefs/${id}/revoke-free-membership`, {}, { withCredentials: true });
    setActionMsg("Free membership revoked");
    loadData();
  };

  const approveChef = async (id) => {
    await axios.post(`${API}/api/admin/chefs/${id}/approve`, {}, { withCredentials: true });
    setActionMsg("Chef approved — now visible in directory");
    loadData();
  };

  const rejectChef = async (id) => {
    if (!window.confirm("Reject this chef's profile? They will see a rejection notice on their dashboard.")) return;
    await axios.post(`${API}/api/admin/chefs/${id}/reject`, {}, { withCredentials: true });
    setActionMsg("Chef rejected");
    loadData();
  };

  const deleteReview = async (id) => {
    if (!window.confirm("Permanently delete this review?")) return;
    await axios.delete(`${API}/api/admin/reviews/${id}`, { withCredentials: true });
    setActionMsg("Review deleted");
    loadReviews();
  };

  const unflagReview = async (id) => {
    await axios.post(`${API}/api/admin/reviews/${id}/unflag`, {}, { withCredentials: true });
    setActionMsg("Review unflagged");
    loadReviews();
  };

  const createPromo = async () => {
    if (!newCode.trim()) return;
    setCreatingPromo(true);
    try {
      await axios.post(`${API}/api/admin/promo-codes`,
        { code: newCode.trim(), description: newDescription.trim() },
        { withCredentials: true });
      setNewCode(""); setNewDescription("");
      setActionMsg("Promo code created");
      loadData();
    } catch (e) {
      setActionMsg(e.response?.data?.detail || "Failed to create promo code");
    } finally {
      setCreatingPromo(false);
    }
  };

  const deletePromo = async (code) => {
    if (!window.confirm(`Delete promo code "${code}"?`)) return;
    try {
      await axios.delete(`${API}/api/admin/promo-codes/${encodeURIComponent(code)}`, { withCredentials: true });
      setActionMsg("Promo code deleted");
      loadData();
    } catch (e) {
      setActionMsg(e.response?.data?.detail || "Failed to delete promo code");
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center pt-16">
      <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#050505] pt-16">
      <div className="border-b border-[#D4AF37]/15 bg-[#080808] py-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-2">
            <Shield size={16} className="text-[#D4AF37]" />
            <p className="text-xs tracking-[0.35em] uppercase text-[#D4AF37]">Administration</p>
          </div>
          <h1 className="font-serif text-4xl text-white font-light">Admin Dashboard</h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        {actionMsg && (
          <div className="mb-6 bg-emerald-900/20 border border-emerald-800/30 text-emerald-400 px-4 py-3 text-sm flex justify-between">
            {actionMsg}
            <button onClick={() => setActionMsg("")} className="hover:text-white">×</button>
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b border-[#D4AF37]/15 mb-8">
          {TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                data-testid={`admin-tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 text-sm tracking-wide transition-colors border-b-2 -mb-px ${
                  activeTab === tab.id ? "border-[#D4AF37] text-[#D4AF37]" : "border-transparent text-[#A0A0A0] hover:text-white"
                }`}
              >
                <Icon size={15} /> {tab.label}
              </button>
            );
          })}
        </div>

        {/* OVERVIEW TAB */}
        {activeTab === "overview" && stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#D4AF37]/10">
            {[
              { label: "Total Chefs", value: stats.total_chefs, icon: Users },
              { label: "Active Listings", value: stats.active_chefs, icon: CheckCircle },
              { label: "Total Clients", value: stats.total_clients, icon: Users },
              { label: "Total Revenue", value: `£${stats.total_revenue?.toFixed(2)}`, icon: TrendingUp },
            ].map(s => {
              const Icon = s.icon;
              return (
                <div key={s.label} data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="bg-[#0A0A0A] p-8">
                  <Icon size={20} className="text-[#D4AF37] mb-4" />
                  <div className="font-serif text-4xl text-white mb-1">{s.value}</div>
                  <div className="text-xs tracking-[0.15em] uppercase text-[#A0A0A0]">{s.label}</div>
                </div>
              );
            })}
          </div>
        )}

        {/* PENDING APPROVALS TAB */}
        {activeTab === "approvals" && (
          <div>
            <p className="text-[#A0A0A0] text-sm mb-6">{pending.length} chef{pending.length === 1 ? "" : "s"} awaiting approval</p>
            {pending.length === 0 ? (
              <div className="bg-[#0A0A0A] border border-[#D4AF37]/10 p-12 text-center">
                <Clock size={32} className="text-[#D4AF37]/40 mx-auto mb-3" />
                <p className="text-[#A0A0A0]">No chefs awaiting approval</p>
                <p className="text-[#555] text-xs mt-2">Free members who redeem a promo code appear here for review.</p>
              </div>
            ) : (
              <div className="space-y-px">
                {pending.map(chef => (
                  <div key={chef._id} data-testid={`pending-chef-${chef._id}`}
                    className="bg-[#0A0A0A] border-l border-[#D4AF37]/40 p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-start gap-4 flex-1 min-w-0">
                      <div className="w-12 h-12 bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                        <span className="font-serif text-[#D4AF37] text-lg">{(chef.chef_name || "?")[0]}</span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-white font-medium">{chef.chef_name || "Unnamed"}</p>
                        <p className="text-[#A0A0A0] text-sm truncate">{chef.chef_email}</p>
                        <div className="flex items-center gap-3 mt-2 flex-wrap">
                          {chef.promo_code_used && (
                            <span className="text-xs px-2 py-0.5 border border-[#D4AF37]/40 text-[#D4AF37] font-mono">
                              {chef.promo_code_used}
                            </span>
                          )}
                          {(chef.regions || []).slice(0, 2).map(r => (
                            <span key={r} className="text-xs text-[#A0A0A0]">{r}</span>
                          ))}
                          {chef.bio ? (
                            <span className="text-xs text-emerald-400">Bio ✓</span>
                          ) : (
                            <span className="text-xs text-amber-400">Missing bio</span>
                          )}
                          {chef.photo_url ? (
                            <span className="text-xs text-emerald-400">Photo ✓</span>
                          ) : (
                            <span className="text-xs text-amber-400">No photo</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <a
                        href={`/chef/${chef._id}`} target="_blank" rel="noreferrer"
                        data-testid={`preview-chef-${chef._id}`}
                        className="border border-white/15 text-[#A0A0A0] hover:text-white hover:border-white/30 px-4 py-2 text-xs tracking-wide transition-colors"
                      >
                        Preview
                      </a>
                      <button
                        data-testid={`approve-chef-${chef._id}`}
                        onClick={() => approveChef(chef._id)}
                        className="flex items-center gap-1.5 bg-emerald-700/20 border border-emerald-700/50 text-emerald-400 hover:bg-emerald-700/40 px-4 py-2 text-xs tracking-wide transition-colors"
                      >
                        <CheckCircle size={13} /> Approve
                      </button>
                      <button
                        data-testid={`reject-chef-${chef._id}`}
                        onClick={() => rejectChef(chef._id)}
                        className="flex items-center gap-1.5 border border-red-900/50 text-red-400 hover:bg-red-950/50 px-4 py-2 text-xs tracking-wide transition-colors"
                      >
                        <XCircle size={13} /> Reject
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* CHEFS TAB */}
        {activeTab === "chefs" && (
          <div>
            <p className="text-[#A0A0A0] text-sm mb-6">{chefs.length} chefs total</p>
            <div className="space-y-px">
              {chefs.length === 0 ? (
                <div className="bg-[#0A0A0A] border border-[#D4AF37]/10 p-12 text-center">
                  <p className="text-[#A0A0A0]">No chefs registered yet</p>
                </div>
              ) : (
                chefs.map(chef => (
                  <div key={chef._id} data-testid={`admin-chef-${chef._id}`}
                    className="bg-[#0A0A0A] border-l border-[#D4AF37]/10 p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                        <span className="font-serif text-[#D4AF37] text-lg">{(chef.chef_name || "?")[0]}</span>
                      </div>
                      <div>
                        <p className="text-white font-medium">{chef.chef_name || "Unnamed"}</p>
                        <p className="text-[#A0A0A0] text-sm">{chef.chef_email}</p>
                        <div className="flex items-center gap-3 mt-2 flex-wrap">
                          <span className={`text-xs px-2 py-0.5 border ${
                            chef.subscription_status === "active"
                              ? "border-emerald-800/50 text-emerald-400"
                              : "border-amber-800/50 text-amber-400"
                          }`}>
                            {chef.subscription_status}
                          </span>
                          <span className={`text-xs px-2 py-0.5 border ${
                            chef.is_active ? "border-blue-800/50 text-blue-400" : "border-red-900/50 text-red-400"
                          }`}>
                            {chef.is_active ? "active" : "suspended"}
                          </span>
                          {chef.is_free_member && (
                            <span data-testid={`free-badge-${chef._id}`}
                              className="flex items-center gap-1 text-xs px-2 py-0.5 border border-[#D4AF37]/60 text-[#D4AF37]">
                              <Gift size={11} /> Free Member
                            </span>
                          )}
                          {(chef.regions || []).length > 0 && (
                            <span className="text-xs text-[#A0A0A0]">{chef.regions[0]}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                      {chef.is_free_member ? (
                        <button
                          data-testid={`revoke-free-${chef._id}`}
                          onClick={() => revokeFree(chef._id)}
                          className="flex items-center gap-1.5 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 px-4 py-2 text-xs tracking-wide transition-colors"
                        >
                          <Gift size={13} /> Revoke Free
                        </button>
                      ) : (
                        <button
                          data-testid={`grant-free-${chef._id}`}
                          onClick={() => grantFree(chef._id)}
                          className="flex items-center gap-1.5 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 px-4 py-2 text-xs tracking-wide transition-colors"
                        >
                          <Gift size={13} /> Grant Free
                        </button>
                      )}
                      {chef.is_active ? (
                        <button
                          data-testid={`suspend-chef-${chef._id}`}
                          onClick={() => suspendChef(chef._id)}
                          className="flex items-center gap-1.5 border border-amber-800/50 text-amber-400 hover:bg-amber-950/50 px-4 py-2 text-xs tracking-wide transition-colors"
                        >
                          <XCircle size={13} /> Suspend
                        </button>
                      ) : (
                        <button
                          data-testid={`activate-chef-${chef._id}`}
                          onClick={() => activateChef(chef._id)}
                          className="flex items-center gap-1.5 border border-emerald-800/50 text-emerald-400 hover:bg-emerald-950/50 px-4 py-2 text-xs tracking-wide transition-colors"
                        >
                          <CheckCircle size={13} /> Activate
                        </button>
                      )}
                      <button
                        data-testid={`delete-chef-${chef._id}`}
                        onClick={() => deleteChef(chef._id)}
                        className="flex items-center gap-1.5 border border-red-900/50 text-red-400 hover:bg-red-950/50 px-4 py-2 text-xs tracking-wide transition-colors"
                      >
                        <Trash2 size={13} /> Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* TRANSACTIONS TAB */}
        {activeTab === "transactions" && (
          <div>
            <p className="text-[#A0A0A0] text-sm mb-6">{transactions.length} transactions</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#D4AF37]/15">
                    {["Email", "Amount", "Currency", "Status", "Payment", "Date"].map(h => (
                      <th key={h} className="text-left py-4 px-4 text-xs tracking-[0.15em] uppercase text-[#D4AF37]">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {transactions.length === 0 ? (
                    <tr><td colSpan={6} className="py-12 text-center text-[#A0A0A0]">No transactions yet</td></tr>
                  ) : (
                    transactions.map(tx => (
                      <tr key={tx._id} data-testid={`tx-${tx._id}`}
                        className="border-b border-white/5 hover:bg-[#0A0A0A] transition-colors">
                        <td className="py-4 px-4 text-[#A0A0A0]">{tx.metadata?.user_email || tx.user_id}</td>
                        <td className="py-4 px-4 text-white font-medium">£{tx.amount}</td>
                        <td className="py-4 px-4 text-[#A0A0A0] uppercase">{tx.currency}</td>
                        <td className="py-4 px-4">
                          <span className={`text-xs px-2 py-1 border ${
                            tx.status === "complete" ? "border-emerald-800/50 text-emerald-400" : "border-amber-800/50 text-amber-400"
                          }`}>{tx.status}</span>
                        </td>
                        <td className="py-4 px-4">
                          <span className={`text-xs px-2 py-1 border ${
                            tx.payment_status === "paid" ? "border-emerald-800/50 text-emerald-400" : "border-amber-800/50 text-amber-400"
                          }`}>{tx.payment_status}</span>
                        </td>
                        <td className="py-4 px-4 text-[#555] text-xs">
                          {tx.created_at ? new Date(tx.created_at).toLocaleDateString("en-GB") : "—"}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* PROMO CODES TAB */}
        {activeTab === "promos" && (
          <div className="space-y-6">
            <div className="bg-[#0A0A0A] border border-[#D4AF37]/15 p-6">
              <p className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] mb-4">Create New Promo Code</p>
              <p className="text-[#A0A0A0] text-sm mb-4">
                Each code is single-use. The chef who redeems it gets free lifetime membership (no expiry, until revoked).
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <input
                  data-testid="new-promo-code-input"
                  type="text"
                  value={newCode}
                  onChange={e => setNewCode(e.target.value.toUpperCase())}
                  placeholder="Code (e.g. LAUNCH-VIP)"
                  className="bg-transparent border border-white/15 focus:border-[#D4AF37] px-4 py-3 text-white placeholder-white/25 text-sm outline-none transition-colors font-mono tracking-wider"
                />
                <input
                  data-testid="new-promo-description-input"
                  type="text"
                  value={newDescription}
                  onChange={e => setNewDescription(e.target.value)}
                  placeholder="Description (optional)"
                  className="bg-transparent border border-white/15 focus:border-[#D4AF37] px-4 py-3 text-white placeholder-white/25 text-sm outline-none transition-colors"
                />
                <button
                  data-testid="create-promo-btn"
                  onClick={createPromo}
                  disabled={creatingPromo || !newCode.trim()}
                  className="flex items-center justify-center gap-2 bg-[#D4AF37] text-black hover:bg-[#F3E5AB] px-6 py-3 text-sm tracking-widest uppercase font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Plus size={14} /> {creatingPromo ? "Creating..." : "Create Code"}
                </button>
              </div>
            </div>

            <p className="text-[#A0A0A0] text-sm">{promos.length} promo code{promos.length === 1 ? "" : "s"}</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#D4AF37]/15">
                    {["Code", "Description", "Status", "Used By", "Created", ""].map(h => (
                      <th key={h} className="text-left py-4 px-4 text-xs tracking-[0.15em] uppercase text-[#D4AF37]">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {promos.length === 0 ? (
                    <tr><td colSpan={6} className="py-12 text-center text-[#A0A0A0]">No promo codes yet</td></tr>
                  ) : (
                    promos.map(p => (
                      <tr key={p.code} data-testid={`promo-${p.code}`}
                        className="border-b border-white/5 hover:bg-[#0A0A0A] transition-colors">
                        <td className="py-4 px-4 text-[#D4AF37] font-mono tracking-wider">{p.code}</td>
                        <td className="py-4 px-4 text-[#A0A0A0]">{p.description || "—"}</td>
                        <td className="py-4 px-4">
                          <span className={`text-xs px-2 py-1 border ${
                            p.used ? "border-amber-800/50 text-amber-400" : "border-emerald-800/50 text-emerald-400"
                          }`}>{p.used ? "used" : "available"}</span>
                        </td>
                        <td className="py-4 px-4 text-[#A0A0A0] text-xs">{p.used_by_email || "—"}</td>
                        <td className="py-4 px-4 text-[#555] text-xs">
                          {p.created_at ? new Date(p.created_at).toLocaleDateString("en-GB") : "—"}
                        </td>
                        <td className="py-4 px-4">
                          <button
                            data-testid={`delete-promo-${p.code}`}
                            onClick={() => deletePromo(p.code)}
                            className="flex items-center gap-1.5 border border-red-900/50 text-red-400 hover:bg-red-950/50 px-3 py-1.5 text-xs tracking-wide transition-colors"
                          >
                            <Trash2 size={12} /> Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* REVIEWS TAB */}
        {activeTab === "reviews" && (
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <button
                data-testid="review-filter-all"
                onClick={() => setReviewFilter("all")}
                className={`px-4 py-2 text-xs tracking-widest uppercase border transition-colors ${
                  reviewFilter === "all" ? "border-[#D4AF37] text-[#D4AF37]" : "border-white/15 text-[#A0A0A0] hover:text-white"
                }`}
              >
                All Reviews
              </button>
              <button
                data-testid="review-filter-flagged"
                onClick={() => setReviewFilter("flagged")}
                className={`flex items-center gap-1.5 px-4 py-2 text-xs tracking-widest uppercase border transition-colors ${
                  reviewFilter === "flagged" ? "border-red-500/60 text-red-400" : "border-white/15 text-[#A0A0A0] hover:text-white"
                }`}
              >
                <Flag size={12} /> Flagged Only
              </button>
            </div>
            <p className="text-[#A0A0A0] text-sm">{reviews.length} review{reviews.length === 1 ? "" : "s"}</p>
            {reviews.length === 0 ? (
              <div className="bg-[#0A0A0A] border border-[#D4AF37]/10 p-12 text-center">
                <MessageSquare size={32} className="text-[#D4AF37]/40 mx-auto mb-3" />
                <p className="text-[#A0A0A0]">No reviews{reviewFilter === "flagged" ? " flagged" : ""} yet</p>
              </div>
            ) : (
              <div className="space-y-px">
                {reviews.map(r => (
                  <div key={r.id} data-testid={`admin-review-${r.id}`}
                    className={`bg-[#0A0A0A] border-l-2 p-5 flex flex-col md:flex-row md:items-start md:justify-between gap-4 ${
                      r.flagged ? "border-red-500/60" : "border-[#D4AF37]/30"
                    }`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap mb-2">
                        <span className="text-white font-medium">{r.reviewer_name}</span>
                        <span className="text-[#555] text-xs">{r.reviewer_email}</span>
                        <div className="flex items-center gap-0.5">
                          {[1, 2, 3, 4, 5].map(n => (
                            <Star key={n} size={13}
                              className={n <= r.rating ? "text-[#D4AF37] fill-[#D4AF37]" : "text-white/15"} />
                          ))}
                        </div>
                        {!r.verified && (
                          <span className="text-xs px-2 py-0.5 border border-amber-800/50 text-amber-400">unverified</span>
                        )}
                        {r.flagged && (
                          <span className="text-xs px-2 py-0.5 border border-red-500/60 text-red-400 flex items-center gap-1">
                            <Flag size={10} /> flagged
                          </span>
                        )}
                      </div>
                      {r.text && <p className="text-[#A0A0A0] text-sm leading-relaxed">{r.text}</p>}
                      {r.flag_reason && (
                        <p className="text-red-300 text-xs mt-2 italic">Flag reason: {r.flag_reason}</p>
                      )}
                      <p className="text-[#555] text-xs mt-2">
                        For chef <a href={`/chef/${r.chef_id}`} target="_blank" rel="noreferrer" className="text-[#D4AF37] hover:underline">{r.chef_id}</a>
                        {r.created_at && <span> · {new Date(r.created_at).toLocaleDateString("en-GB")}</span>}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {r.flagged && (
                        <button
                          data-testid={`unflag-review-${r.id}`}
                          onClick={() => unflagReview(r.id)}
                          className="flex items-center gap-1.5 border border-white/15 text-[#A0A0A0] hover:text-white hover:border-white/30 px-3 py-1.5 text-xs tracking-wide transition-colors"
                        >
                          <Flag size={12} /> Unflag
                        </button>
                      )}
                      <button
                        data-testid={`delete-review-${r.id}`}
                        onClick={() => deleteReview(r.id)}
                        className="flex items-center gap-1.5 border border-red-900/50 text-red-400 hover:bg-red-950/50 px-3 py-1.5 text-xs tracking-wide transition-colors"
                      >
                        <Trash2 size={12} /> Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
